import { cn } from '@/lib/utils';

export type VerifiedBadgeSize = 'sm' | 'md' | 'lg';

type VerifiedType = 'none' | 'blue' | 'business' | 'government';
type VisibleVerifiedType = Exclude<VerifiedType, 'none'>;

interface VerifiedVariantMeta {
  colorClass: string;
  label: string;
}

interface VerifiedBadgeProperties {
  className?: string;
  showLabel?: boolean;
  size?: VerifiedBadgeSize;
  type: VerifiedType; // required: always pass a type
}

const SIZE_CLASS_BY_SIZE = new Map<VerifiedBadgeSize, string>([
  ['sm', 'w-3.5 h-3.5'],
  ['md', 'w-4 h-4'],
  ['lg', 'w-5 h-5'],
]);

const VARIANT_META_BY_TYPE = new Map<VisibleVerifiedType, VerifiedVariantMeta>([
  [
    'business',
    {
      colorClass: 'text-[hsl(var(--brand-verified-business))]',
      label: 'Verified (business)',
    },
  ],
  [
    'government',
    {
      colorClass: 'text-[hsl(var(--brand-verified-government))]',
      label: 'Verified (government)',
    },
  ],
  [
    'blue',
    {
      colorClass: 'text-[hsl(var(--brand-twitter-blue))]',
      label: 'Verified',
    },
  ],
]);

const expectMapValue = <Key, Value>(map: ReadonlyMap<Key, Value>, key: Key, context: string): Value => {
  const value = map.get(key);
  if (value === undefined) {
    throw new Error(`Unknown ${context}: ${String(key)}`);
  }
  return value;
};

export function VerifiedBadge({ className = '', size = 'md', type, showLabel = false }: VerifiedBadgeProperties) {
  if (type === 'none') {
    return null;
  }

  const sizeClass = expectMapValue(SIZE_CLASS_BY_SIZE, size, 'verified badge size');
  // Twitter brand requirement: the blue verified badge must use the canonical #1D9BF0 color.
  const { colorClass, label } = expectMapValue(VARIANT_META_BY_TYPE, type, 'verified badge type');

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <svg
        className={cn(sizeClass, colorClass, 'flex-shrink-0')}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-label={label}
      >
        <title>{label}</title>
        <path d="M20.396 11c-.018-.646-.215-1.275-.57-1.816-.354-.54-.852-.972-1.438-1.246.223-.607.27-1.264.14-1.897-.131-.634-.437-1.218-.882-1.687-.47-.445-1.053-.75-1.687-.882-.633-.13-1.29-.083-1.897.14-.273-.587-.704-1.086-1.245-1.44S11.647 1.62 11 1.604c-.646.017-1.273.213-1.813.568s-.969.854-1.24 1.44c-.608-.223-1.267-.272-1.902-.14-.635.13-1.22.436-1.69.882-.445.47-.749 1.055-.878 1.688-.13.633-.08 1.29.144 1.896-.587.274-1.087.705-1.443 1.245-.356.54-.555 1.17-.574 1.817.02.647.218 1.276.574 1.817.356.54.856.972 1.443 1.245-.224.606-.274 1.263-.144 1.896.13.634.433 1.218.877 1.688.47.443 1.054.747 1.687.878.633.132 1.29.084 1.897-.136.274.586.705 1.084 1.246 1.439.54.354 1.17.551 1.816.569.647-.016 1.276-.213 1.817-.567s.972-.854 1.245-1.44c.604.239 1.266.296 1.903.164.636-.132 1.22-.447 1.68-.907.46-.46.776-1.044.908-1.681s.075-1.299-.165-1.903c.586-.274 1.084-.705 1.439-1.246.354-.54.551-1.17.569-1.816zM9.662 14.85l-3.429-3.428 1.293-1.302 2.072 2.072 4.4-4.794 1.347 1.246z" />
      </svg>
      {showLabel && <span className="text-xs text-muted-foreground">{label}</span>}
    </span>
  );
}
